import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import {
  getAllEvents,
  deleteEvent,
  toggleEventActive,
} from "./../../services/eventServices";

import Button from "./../../components/Button/Button";
import ButtonEdit from "./../../components/Button/ButtonEdit";
import ButtonDelete from "./../../components/Button/ButtonDelete";

import {
  FaPlusSquare,
  FaInfoCircle,
  FaTrophy,
  FaChromecast,
  FaSortAmountDownAlt,
  FaSortAmountUpAlt,
  FaFilter,
} from "react-icons/fa";

import formatDate from "./../../utils/formatDate";
import CreateEventModal from "./CreateEventModal";
import EditEventModal from "./EditEventModal";
import EventDetailsModal from "./../../pages/CalendarPage/EventDetailsModal";
import RewardsEditModal from "./RewardsEditModal";

import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import { ToastContainer, toast } from "react-toastify";

const FALLBACK_IMG = "/img/default_image.jpg";

/** Petit helper d’affichage */
const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-slate-200/70 dark:border-slate-700/70 bg-white/70 dark:bg-slate-800/60 px-2 py-0.5 text-[11px] font-semibold text-slate-700 dark:text-slate-200">
    {children}
  </span>
);

const IconBtn = ({ title, onClick, className = "", children }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={[
      "w-9 h-9 inline-flex items-center justify-center rounded-xl shadow-sm transition",
      "bg-slate-900 text-white hover:opacity-90 active:scale-[0.98]",
      "dark:bg-slate-200 dark:text-slate-900",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const EventsPage = () => {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isRewardsModalOpen, setIsRewardsModalOpen] = useState(false);

  const [editableEvent, setEditableEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [sortAscending, setSortAscending] = useState(true);
  const [filter, setFilter] = useState("upcoming");
  const [typeFilter, setTypeFilter] = useState("All");
  const [levelFilter, setLevelFilter] = useState("All");
  const [cateFilter, setCateFilter] = useState("All");

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des événements", error);
      toast.error("Impossible de charger les événements.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const openEditModal = (event) => {
    setEditableEvent(event);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => setIsEditModalOpen(false);

  const openDetailsModal = (event) => {
    setSelectedEvent(event);
    setIsDetailsModalOpen(true);
  };
  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedEvent(null);
  };

  const openRewardsModal = (event) => {
    setEditableEvent(event);
    setIsRewardsModalOpen(true);
  };
  const closeRewardsModal = () => setIsRewardsModalOpen(false);

  const deleteEventFunc = async (id) => {
    try {
      await deleteEvent(id);
      setEvents((prev) => prev.filter((event) => event.id !== id));
      toast.success("Événement supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression de l'événement", error);
      toast.error("Erreur lors de la suppression de l'événement.");
    }
  };

  const handleToggleActive = async (event) => {
    try {
      const updatedEvent = await toggleEventActive(event.id);
      setEvents((prevEvents) =>
        prevEvents.map((e) =>
          e.id === event.id ? { ...e, is_active: updatedEvent.is_active } : e
        )
      );
    } catch (error) {
      console.error(
        "Erreur lors du changement de l'état de l'événement",
        error
      );
      toast.error("Erreur lors du changement de l'état de l'événement.");
    }
  };

  const isPastDate = (date) =>
    new Date(date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);

  const toggleSortOrder = () => setSortAscending((v) => !v);

  /** ✅ Nouveau: ouvre la page de casting dans un nouvel onglet */
  const openCastingPage = (event) => {
    if (!event?.id) return;

    try {
      localStorage.setItem(`casting_event_${event.id}`, JSON.stringify(event));
    } catch (e) {}

    const url = `/casting/${encodeURIComponent(event.id)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const typeEvents = useMemo(
    () => [
      "Compétition",
      "Animation",
      "Stage & Regroupement Technique",
      "Passage de Grade",
      "Entraînement Départemental",
      "Stage Sportif Départemental",
      "Cours commun Club",
      "Stage Club",
      "Tournoi Interclubs",
      "Autre",
    ],
    []
  );

  const levelEvents = useMemo(
    () => ["Club", "Départemental", "Régional", "National", "International"],
    []
  );

  const cateEvents = useMemo(
    () => [
      "Eveil",
      "Mini-Poussin(e)",
      "Poussin(e)",
      "Benjamin(e)",
      "Minime",
      "Cadet(te)",
      "Junior",
      "Sénior",
      "Vétéran",
    ],
    []
  );

  const computedEvents = useMemo(() => {
    const now = new Date().setHours(0, 0, 0, 0);

    const filtered = (events || []).filter((event) => {
      const eventDate = new Date(event.start).setHours(0, 0, 0, 0);
      const categories = event.cate_event ? event.cate_event.split(",") : [];

      return (
        (filter === "all" ||
          (filter === "upcoming" && eventDate >= now) ||
          (filter === "past" && eventDate < now)) &&
        (typeFilter === "All" || event.type_event === typeFilter) &&
        (levelFilter === "All" || event.level_event === levelFilter) &&
        (cateFilter === "All" || categories.includes(cateFilter))
      );
    });

    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.start);
      const dateB = new Date(b.start);
      return sortAscending ? dateA - dateB : dateB - dateA;
    });

    return sorted;
  }, [events, filter, typeFilter, levelFilter, cateFilter, sortAscending]);

  return (
    <div className="min-h-screen w-full bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
      <div className="mx-auto w-full max-w-6xl px-3 sm:px-6 pt-[130px] md:pt-[150px] pb-10">
        <ScrollIndicator />
        {/* Header */}
        <div className="mb-6 rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-950/40 shadow-sm px-4 sm:px-6 py-5">
          <p className="text-xs tracking-[0.35em] uppercase text-slate-500 dark:text-slate-400 text-center">
            Administrateurs
          </p>
          <h2 className="mt-2 text-center text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-wide font-montserrat uppercase">
            Liste des événements
          </h2>

          <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <Button
              label="Ajouter un événement"
              text="Ajouter un événement"
              icon={FaPlusSquare}
              onClick={() => setIsCreateModalOpen(true)}
              className="gap-2 w-full sm:w-auto"
            />

            <div className="flex items-center justify-between sm:justify-end gap-2">
              <div className="text-sm text-slate-600 dark:text-slate-300">
                {loading
                  ? "Chargement…"
                  : `${computedEvents.length} événement(s)`}
              </div>

              <button
                type="button"
                onClick={toggleSortOrder}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/80 dark:bg-slate-900/60 px-3 py-2 text-sm font-semibold hover:bg-white dark:hover:bg-slate-900 transition"
                title="Trier par date"
              >
                {sortAscending ? (
                  <FaSortAmountDownAlt />
                ) : (
                  <FaSortAmountUpAlt />
                )}
                <span className="hidden sm:inline">
                  {sortAscending ? "Date croissante" : "Date décroissante"}
                </span>
              </button>
            </div>
          </div>
        </div>
        {/* Filters */}
        <div className="mb-6 rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-950/40 shadow-sm px-4 sm:px-6 py-5">
          <div className="flex items-center gap-2 mb-4 text-slate-700 dark:text-slate-200">
            <FaFilter />
            <h3 className="font-bold">Filtres</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 font-montserrat">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/90 dark:bg-slate-900/60 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-400"
            >
              <option value="all">Tous</option>
              <option value="upcoming">À venir</option>
              <option value="past">Passés</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/90 dark:bg-slate-900/60 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-400"
            >
              <option value="All">Tous les types</option>
              {typeEvents.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="w-full rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/90 dark:bg-slate-900/60 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-400"
            >
              <option value="All">Tous les niveaux</option>
              {levelEvents.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>

            <select
              value={cateFilter}
              onChange={(e) => setCateFilter(e.target.value)}
              className="w-full rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/90 dark:bg-slate-900/60 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-400"
            >
              <option value="All">Toutes les catégories</option>
              {cateEvents.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>
        {/* ✅ DESKTOP TABLE : remplace uniquement le bloc "Desktop table" par
        celui-ci // (Date avant Événement, pas d’ascenseur horizontal, bouton
        Chromecast fond bleu) */}
        <div className="hidden md:block rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-950/40 shadow-sm overflow-hidden">
          <table className="w-full table-fixed border-collapse">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="w-20 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                  Actif
                </th>
                <th className="w-36 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Événement
                </th>
                <th className="w-[220px] px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200/70 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-8 text-center text-slate-600 dark:text-slate-300"
                  >
                    Chargement…
                  </td>
                </tr>
              ) : computedEvents.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-8 text-center text-slate-600 dark:text-slate-300"
                  >
                    Aucun événement.
                  </td>
                </tr>
              ) : (
                computedEvents.map((event) => {
                  const isPastEvent = isPastDate(event.start);
                  const imgSrc = event?.photo_url || FALLBACK_IMG;

                  return (
                    <tr
                      key={event.id}
                      className={[
                        "bg-white/60 dark:bg-slate-900/40",
                        "hover:bg-slate-50 dark:hover:bg-slate-900/70 transition",
                        isPastEvent ? "opacity-80" : "",
                      ].join(" ")}
                    >
                      {/* Actif */}
                      <td
                        className="px-4 py-4 text-center cursor-pointer select-none"
                        onClick={() => handleToggleActive(event)}
                        title="Activer / désactiver"
                      >
                        <span
                          className={[
                            "inline-flex items-center justify-center w-9 h-9 rounded-xl",
                            event.is_active
                              ? "bg-emerald-600 text-white"
                              : "bg-rose-600 text-white",
                          ].join(" ")}
                        >
                          {event.is_active ? "✅" : "❌"}
                        </span>
                      </td>

                      {/* ✅ Date AVANT événement */}
                      <td className="px-4 py-4 text-center font-semibold whitespace-nowrap">
                        {formatDate(event.start)}
                      </td>

                      {/* Événement */}
                      <td className="px-4 py-4">
                        <div className="flex gap-4 items-center min-w-0">
                          <div className="w-20 h-12 rounded-2xl overflow-hidden border border-slate-200/70 dark:border-slate-700/70 bg-slate-100 dark:bg-slate-800 shrink-0">
                            <img
                              src={imgSrc}
                              alt={event.titre}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = FALLBACK_IMG;
                              }}
                            />
                          </div>

                          <div className="min-w-0">
                            <div className="font-bold truncate">
                              {event.titre}
                            </div>

                            <div className="mt-1 flex flex-wrap gap-2">
                              {event.type_event && (
                                <Badge>{event.type_event}</Badge>
                              )}
                              {event.level_event && (
                                <Badge>{event.level_event}</Badge>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() => openEditModal(event)}
                              className="mt-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white underline underline-offset-4"
                            >
                              Modifier image / infos
                            </button>
                          </div>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <IconBtn
                            title="Détails"
                            onClick={() => openDetailsModal(event)}
                          >
                            <FaInfoCircle />
                          </IconBtn>

                          <IconBtn
                            title="Récompenses"
                            onClick={() => openRewardsModal(event)}
                          >
                            <FaTrophy />
                          </IconBtn>

                          {/* ✅ Bouton CAST (Chromecast) fond bleu */}
                          <IconBtn
                            title="Ouvrir la page Casting (nouvel onglet)"
                            onClick={() => openCastingPage(event)}
                            className="bg-sky-600 hover:bg-sky-500 text-white dark:bg-sky-500 dark:hover:bg-sky-400"
                          >
                            <FaChromecast className="w-5 h-5" />
                          </IconBtn>

                          <ButtonEdit onClick={() => openEditModal(event)} />
                          <ButtonDelete
                            onClick={() => deleteEventFunc(event.id)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {loading ? (
            <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-950/40 p-5 text-center">
              Chargement…
            </div>
          ) : computedEvents.length === 0 ? (
            <div className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-950/40 p-5 text-center">
              Aucun événement.
            </div>
          ) : (
            computedEvents.map((event) => {
              const imgSrc = event?.photo_url || FALLBACK_IMG;

              return (
                <div
                  key={event.id}
                  className="rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-950/40 shadow-sm p-4"
                >
                  <div className="flex gap-3">
                    <div className="w-24 h-16 rounded-2xl overflow-hidden border border-slate-200/70 dark:border-slate-700/70 bg-slate-100 dark:bg-slate-800 shrink-0">
                      <img
                        src={imgSrc}
                        alt={event.titre}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK_IMG;
                        }}
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="font-extrabold leading-tight truncate">
                        {event.titre}
                      </div>
                      <div className="mt-1 text-sm font-semibold">
                        {formatDate(event.start)}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {event.type_event && <Badge>{event.type_event}</Badge>}
                        {event.level_event && (
                          <Badge>{event.level_event}</Badge>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleToggleActive(event)}
                      className={[
                        "w-10 h-10 rounded-2xl text-white font-bold shadow-sm shrink-0",
                        event.is_active ? "bg-emerald-600" : "bg-rose-600",
                      ].join(" ")}
                      title="Activer / désactiver"
                    >
                      {event.is_active ? "✅" : "❌"}
                    </button>
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-2">
                    <div className="flex gap-2">
                      <IconBtn
                        title="Détails"
                        onClick={() => openDetailsModal(event)}
                      >
                        <FaInfoCircle />
                      </IconBtn>
                      <IconBtn
                        title="Récompenses"
                        onClick={() => openRewardsModal(event)}
                      >
                        <FaTrophy />
                      </IconBtn>
                      <IconBtn
                        title="Casting (nouvel onglet)"
                        onClick={() => openCastingPage(event)}
                        className="bg-sky-600 hover:bg-sky-500 text-white dark:bg-sky-500 dark:hover:bg-sky-400"
                      >
                        <FaChromecast className="w-5 h-5" />
                      </IconBtn>
                    </div>

                    <div className="flex gap-2">
                      <ButtonEdit onClick={() => openEditModal(event)} />
                      <ButtonDelete onClick={() => deleteEventFunc(event.id)} />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => openEditModal(event)}
                    className="mt-3 w-full text-center text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white underline underline-offset-4"
                  >
                    Modifier image / infos
                  </button>
                </div>
              );
            })
          )}
        </div>
        {/* Modals */}
        <EditEventModal
          isOpen={isEditModalOpen}
          onClose={closeEditModal}
          event={editableEvent}
          refreshList={fetchEvents}
        />
        <CreateEventModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          refreshList={fetchEvents}
        />
        <EventDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={closeDetailsModal}
          event={selectedEvent}
          userRole={userRole}
        />
        <RewardsEditModal
          isOpen={isRewardsModalOpen}
          onClose={closeRewardsModal}
          event={editableEvent}
          refreshList={fetchEvents}
        />
        <ToastContainer />
      </div>
    </div>
  );
};

export default EventsPage;
